import { useState } from 'react';

import { Button } from '../../components/Button';
import { Divider } from '../../components/Divider';
import { Dropdown } from '../../components/Dropdown';
import { InfoNote } from '../../components/InfoNote';
import { PageHeading } from '../../components/PageHeading';
import { SectionHeader } from '../../components/SectionHeader';
import { SideImage } from '../../components/SideImage';
import { BookingTopBar } from '../../composites/BookingTopBar';
import { PreferencesPanel } from '../../composites/PreferencesPanel';
import { ReservationPanel } from '../../composites/ReservationPanel';
import { SplitLayout } from '../../composites/SplitLayout';
import { CloseIcon } from '../../icons/CloseIcon';
import { InfoIcon } from '../../icons/InfoIcon';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import {
  BottomBar,
  BottomBarTitle,
  CardSection,
  Content,
  DepositBanner,
  EmptyTimeslots,
  FieldLabel,
  DepositDismiss,
  DepositText,
  FormPanel,
  MenuCard,
  MenuField,
  Notices,
  PanelStack,
  TimeslotGrid,
  RestaurantList,
  RestaurantName,
  RestaurantOption,
  RestaurantOptionText,
  RestaurantSubtitle,
  RestaurantThumb,
  RestaurantThumbPlaceholder,
} from './DiningReservationTemplate.styles';
import type { DiningReservationTemplateProps } from './DiningReservationTemplate.types';

const CLEAR_MENU_VALUE = '';

const noop = () => undefined;

export function DiningReservationTemplate({
  imageUrl,
  imageAlt,
  onBack,
  progressStep = 1,
  progressTotal = 2,
  restaurants,
  selectedRestaurantId,
  onSelectRestaurant,
  guestCount,
  minGuests,
  maxGuests,
  guestStep,
  onGuestCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  maxDate,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  timeslots,
  isLoading,
  selectedTimeslot,
  onTimeslotSelect,
  menuOptions,
  selectedMenuId,
  onMenuSelect,
  onMenuClear,
  menuRequired = false,
  leaveTimeNote,
  depositMessage,
  preferenceCategories,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onReserve,
  reserveDisabled,
  labels,
}: DiningReservationTemplateProps) {
  const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId) ?? null;
  const restaurantSelected = selectedRestaurant !== null;
  const [dismissedDepositMessage, setDismissedDepositMessage] = useState<string>();
  const dateSelected = restaurantSelected && Boolean(selectedDate);
  const hasTimeslots = Boolean(timeslots?.length);
  const showMenu = restaurantSelected && Boolean(menuOptions?.length);
  const selectedMenuLabel = menuOptions?.find((option) => option.value === selectedMenuId)?.label;
  const menuFieldLabel = labels.menuFieldLabel ?? 'Menu';
  const menuPlaceholder = menuRequired ? `${menuFieldLabel}*` : menuFieldLabel;
  const menuName = selectedMenuLabel
    ? [labels.menuTitle, selectedMenuLabel].filter(Boolean).join(': ')
    : labels.menuTitle ?? menuPlaceholder;
  const menuAriaLabel =
    menuRequired && labels.menuRequiredLabel
      ? `${menuName}, ${labels.menuRequiredLabel}`
      : menuName;
  const menuClearLabel =
    !menuRequired && onMenuClear && selectedMenuLabel ? labels.menuClearLabel : undefined;
  const menuDropdownOptions = menuClearLabel
    ? [{ value: CLEAR_MENU_VALUE, label: menuClearLabel }, ...(menuOptions ?? [])]
    : menuOptions ?? [];
  const handleMenuChange = (value: string) => {
    if (value === CLEAR_MENU_VALUE) {
      onMenuClear?.();
      return;
    }
    onMenuSelect?.(value);
  };
  const showDeposit = Boolean(depositMessage) && depositMessage !== dismissedDepositMessage;
  const showPreferences = restaurantSelected && Boolean(preferenceCategories?.length);
  const canReserve =
    restaurantSelected &&
    Boolean(selectedDate) &&
    Boolean(selectedTimeslot) &&
    (!menuRequired || Boolean(selectedMenuLabel));

  return (
    <SplitLayout
      leftWidth="45%"
      height="100vh"
      flushRightContent
      containedScroll
      left={<SideImage src={imageUrl} alt={imageAlt} sticky />}
      right={
        <>
          <BookingTopBar
            onBack={onBack}
            backLabel={labels.back}
            progressStep={progressStep}
            progressTotal={progressTotal}
          />

          <Content>
            <PageHeading title={labels.heading} subtitle={labels.subheading} centered />

            <PanelStack>
              <FormPanel>
                <CardSection $wide>
                  <SectionHeader
                    title={labels.restaurantTitle}
                    subtitle={labels.restaurantSubtitle}
                  />
                  <RestaurantList>
                    {restaurants.map((restaurant) => {
                      const isSelected = restaurant.id === selectedRestaurantId;
                      return (
                        <RestaurantOption
                          key={restaurant.id}
                          type="button"
                          $selected={isSelected}
                          aria-pressed={isSelected}
                          onClick={isSelected ? undefined : () => onSelectRestaurant(restaurant.id)}
                        >
                          <RestaurantOptionText>
                            <RestaurantName>{restaurant.name}</RestaurantName>
                            {restaurant.subtitle && (
                              <RestaurantSubtitle>{restaurant.subtitle}</RestaurantSubtitle>
                            )}
                          </RestaurantOptionText>
                          {restaurant.imageUrl ? (
                            <RestaurantThumb src={restaurant.imageUrl} alt={restaurant.name} />
                          ) : (
                            <RestaurantThumbPlaceholder aria-hidden />
                          )}
                        </RestaurantOption>
                      );
                    })}
                  </RestaurantList>
                </CardSection>

                {restaurantSelected && (
                  <>
                    <Divider spacing="none" />
                    <CardSection>
                      <ReservationPanel
                        title={labels.reservationTitle}
                        subtitle={labels.reservationSubtitle}
                        guestLabel={labels.guestLabel}
                        guestCount={guestCount}
                        minGuests={minGuests}
                        maxGuests={maxGuests}
                        guestStep={guestStep}
                        onGuestCountChange={onGuestCountChange}
                        activeDate={activeDate}
                        selectedDate={selectedDate}
                        calendarView={calendarView}
                        availability={availability}
                        maxDate={maxDate}
                        onDateSelect={onDateSelect}
                        onWeekNavigate={onWeekNavigate}
                        onCalendarViewChange={onCalendarViewChange}
                        calendarLabels={labels.calendar}
                        counterDecreaseLabel={labels.counter?.decrease}
                        counterIncreaseLabel={labels.counter?.increase}
                      />

                      {dateSelected && (
                        <>
                          <Divider spacing="none" />
                          <FieldLabel>{labels.timeLabel}</FieldLabel>
                          {hasTimeslots && timeslots ? (
                            <TimeslotGrid>
                              {timeslots.map((slot) => (
                                <Button
                                  key={slot.value}
                                  variant="toggle"
                                  size="small"
                                  fullWidth
                                  selected={slot.value === selectedTimeslot}
                                  onClick={() => onTimeslotSelect(slot.value)}
                                  type="button"
                                >
                                  {slot.label}
                                </Button>
                              ))}
                            </TimeslotGrid>
                          ) : isLoading ? null : (
                            <EmptyTimeslots>{labels.noTimeslotsLabel}</EmptyTimeslots>
                          )}
                        </>
                      )}

                      {showMenu && menuOptions && (
                        <MenuCard>
                          <FieldLabel>{labels.menuTitle}</FieldLabel>
                          <MenuField>
                            <Dropdown
                              options={menuDropdownOptions}
                              value={selectedMenuId}
                              onChange={handleMenuChange}
                              placeholder={menuPlaceholder}
                              aria-label={menuAriaLabel}
                              align="left"
                            />
                          </MenuField>
                        </MenuCard>
                      )}

                      {(leaveTimeNote || showDeposit) && (
                        <Notices>
                          {leaveTimeNote && (
                            <InfoNote text={leaveTimeNote} icon={<TimeChangeIcon size={18} />} />
                          )}
                          {showDeposit && (
                            <DepositBanner>
                              <InfoIcon />
                              <DepositText>{depositMessage}</DepositText>
                              <DepositDismiss
                                type="button"
                                aria-label={labels.dismiss ?? 'Dismiss'}
                                onClick={() => setDismissedDepositMessage(depositMessage)}
                              >
                                <CloseIcon />
                              </DepositDismiss>
                            </DepositBanner>
                          )}
                        </Notices>
                      )}
                    </CardSection>
                  </>
                )}
              </FormPanel>

              {showPreferences && preferenceCategories && (
                <FormPanel>
                  <CardSection $compact>
                    <PreferencesPanel
                      categories={preferenceCategories}
                      onOptionToggle={onPreferenceOptionToggle ?? noop}
                      onOtherChange={onPreferenceOtherChange ?? noop}
                      onFreeTextChange={onPreferenceFreeTextChange ?? noop}
                      otherButtonLabel={labels.preferences?.otherButtonLabel}
                    />
                  </CardSection>
                </FormPanel>
              )}
            </PanelStack>
          </Content>

          <BottomBar>
            <BottomBarTitle>{selectedRestaurant?.name ?? ''}</BottomBarTitle>
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={onReserve}
              disabled={(reserveDisabled ?? false) || !canReserve}
            >
              {labels.reserveLabel}
            </Button>
          </BottomBar>
        </>
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
